import type {ThemeDefinition} from "./types"

const REQUIRED_ILLUSTRATION_KEYS = [
  "potentialCauses",
  "likelyCauses",
  "recommend",
  "diySteps",
] as const

/**
 * Validate that a theme definition has every required configuration group
 * filled in. Runs once per active theme so a missing asset/config fails the
 * build instead of surfacing as a broken image at runtime.
 *
 * Kept out of `registry.ts` so the client bundle can import this check
 * without pulling every brand module into one UMD.
 */
export const assertThemeDefinitionComplete = (
  definition: ThemeDefinition,
): void => {
  const missingAssets = (
    [
      "landingLogo",
      "compactMark",
      "accentGlyph",
      "footerMark",
      "feedbackMark",
      "positiveConfirmIcon",
    ] as const
  ).filter((key) => !definition.assets[key])

  const missingIllustrations = REQUIRED_ILLUSTRATION_KEYS.filter(
    (key) => !definition.assets.illustrations[key],
  )

  if (missingAssets.length > 0 || missingIllustrations.length > 0) {
    const missing = [
      ...missingAssets,
      ...missingIllustrations.map((key) => `illustrations.${key}`),
    ]
    throw new Error(
      `Theme "${definition.id}" is missing required asset(s): ${missing.join(", ")}.`,
    )
  }

  if (
    definition.capabilities.chatGreetingIcon === "emblemImage" &&
    !definition.assets.chatEmblem
  ) {
    throw new Error(
      `Theme "${definition.id}" sets chatGreetingIcon=emblemImage but assets.chatEmblem is missing.`,
    )
  }

  if (
    !definition.tokens ||
    !definition.chrome ||
    !definition.links ||
    !definition.fonts ||
    !definition.capabilities
  ) {
    throw new Error(
      `Theme "${definition.id}" is missing one of: tokens, chrome, links, fonts, capabilities.`,
    )
  }

  if (definition.capabilities.remediationCopyVariant === "applianceService") {
    if (!definition.remediationCopy) {
      throw new Error(
        `Theme "${definition.id}" sets remediationCopyVariant=applianceService but remediationCopy is missing.`,
      )
    }
  } else if (definition.remediationCopy) {
    throw new Error(
      `Theme "${definition.id}" sets remediationCopyVariant=standard but remediationCopy is present.`,
    )
  }
}
